import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import {
  BiUpdateDatasetRequest,
  kBiMaxDatasetName,
} from '@euler/model/bi/library';
import { kBiMaxDatasetDescription } from '@euler/model/bi/upload';
import { memo, useEffect, useState } from 'react';
import { TextInput, View } from 'react-native';
import { BiButton } from '../../components/ui/BiButton';

/** Renames and describes an uploaded dataset (`manage`). */
export const DatasetEditPanel = memo(
  ({
    visible,
    dataset,
    busy,
    error,
    onSave,
    onClose,
  }: {
    visible: boolean;
    dataset: BiUploadedDatasetView;
    busy: boolean;
    /** Why the last save failed, shown in place. */
    error: string | null;
    onSave: (request: BiUpdateDatasetRequest) => void;
    onClose: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [name, setName] = useState(dataset.name);
    const [description, setDescription] = useState(dataset.description ?? '');
    useEffect(() => {
      if (!visible) return;
      setName(dataset.name);
      setDescription(dataset.description ?? '');
    }, [visible, dataset]);
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const request: BiUpdateDatasetRequest = {
      ...(trimmedName !== dataset.name ? { name: trimmedName } : {}),
      ...(trimmedDescription !== (dataset.description ?? '')
        ? { description: trimmedDescription || null }
        : {}),
    };
    const changed = Object.keys(request).length > 0;
    const input = {
      paddingHorizontal: rpx(12),
      borderRadius: rpx(10),
      borderWidth: 1,
      borderColor: aiColors.border,
      backgroundColor: aiColors.input,
      color: aiColors.foreground,
      fontSize: rpx(14),
    };
    return (
      <BottomPanel visible={visible} onClose={onClose} fitContent>
        <View style={{ padding: rpx(24), gap: rpx(12) }}>
          <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
            编辑数据
          </Typography.Text>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            名称
          </Typography.Text>
          <TextInput
            accessibilityLabel="数据名称"
            value={name}
            onChangeText={setName}
            maxLength={kBiMaxDatasetName}
            placeholder="例如：2025 配件采购"
            placeholderTextColor={aiColors.mutedForeground}
            style={[input, { height: rpx(40) }]}
          />
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            说明
          </Typography.Text>
          <TextInput
            accessibilityLabel="数据说明"
            value={description}
            onChangeText={setDescription}
            maxLength={kBiMaxDatasetDescription}
            multiline
            placeholder="这份数据是什么，每一行代表什么"
            placeholderTextColor={aiColors.mutedForeground}
            style={[
              input,
              {
                minHeight: rpx(80),
                paddingVertical: rpx(8),
                textAlignVertical: 'top',
              },
            ]}
          />
          {error ? (
            <Typography.Text size={rpx(12)} color={aiColors.destructive}>
              {error}
            </Typography.Text>
          ) : null}
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: rpx(8),
            }}
          >
            <BiButton title="取消" onPress={onClose} />
            <BiButton
              title="保存"
              tone="primary"
              loading={busy}
              disabled={!changed || !trimmedName}
              onPress={() => onSave(request)}
            />
          </View>
        </View>
      </BottomPanel>
    );
  },
);
